import type { VehicleDimensions } from '@/lib/vehicle-type-utils';
import type { EventDateRangeType, ReservationSearchLayout } from '@/types/reserve.types';

export enum ReservationMode {
  hourly = 'hourly',
  monthly = 'monthly',
}

/** Display-ready option for a generic vehicle type */
export interface VehicleTypeOption {
  value: string;
  label: string;
}

/** Display-ready saved vehicle for the dropdown */
export interface SavedVehicleOption {
  id: string;
  name: string;
  meta: string;
  make: string | undefined;
  oversizeType: string;
  /** Inches, from the make/model catalogue. Absent when the vehicle was entered manually. */
  height?: number;
  length?: number;
  /** Display-ready: false when the vehicle is missing a flow-Required field (PB-2555); defaults to valid — the search-bar dropdown has no garage in scope, so gating is a no-op there. */
  valid?: boolean;
  /** Display-ready: "{field(s)} is/are required" message when invalid. */
  requirementMessage?: string;
}

/** The result of a vehicle selection: either a saved vehicle or a generic type */
export type VehicleSelection =
  | { kind: 'type'; value: string }
  | { kind: 'saved'; vehicle: SavedVehicleOption };

/** Extract the oversizeType string from any VehicleSelection */
export function getOversizeType(selection: VehicleSelection): string {
  return selection.kind === 'type' ? selection.value : selection.vehicle.oversizeType;
}

/** Null for a generic type pick — the signal that there is nothing to measure. */
export function getVehicleDimensions(selection: VehicleSelection): VehicleDimensions | null {
  if (selection.kind === 'type') return null;
  return { height: selection.vehicle.height, length: selection.vehicle.length };
}

/** Get display text for a VehicleSelection */
export function getVehicleDisplayText(
  selection: VehicleSelection,
  typeOptions: VehicleTypeOption[]
): string {
  if (selection.kind === 'saved') return selection.vehicle.name;
  const option = typeOptions.find((o) => o.value === selection.value);
  return option?.label ?? selection.value;
}

export interface SearchBarProps {
  /** Current reservation mode (hourly/monthly) */
  mode: ReservationMode;
  /** Called when mode changes */
  onModeChange: (mode: ReservationMode) => void;
  /** Destination display text */
  destinationText: string;
  /** Called when the destination field is clicked */
  onDestinationClick: () => void;
  /** Enter datetime */
  enterDate: Date | null;
  /** Called when enter date changes */
  onEnterDateChange: (date: Date | null) => void;
  /** Exit datetime */
  exitDate: Date | null;
  /** Called when exit date changes */
  onExitDateChange: (date: Date | null) => void;
  /** Monthly permit start date (independent of the hourly enter/exit window) */
  permitStartDate: Date | null;
  /** Called when the permit start date changes */
  onPermitStartChange: (date: Date | null) => void;
  /** Duration display text (e.g. "1h", "3h") */
  durationText: string;
  /** Current duration in minutes as a string (Select value) */
  durationMinutes: string;
  /** Called when duration is selected from the dropdown */
  onDurationChange: (minutes: string) => void;
  /** Current vehicle selection */
  vehicleSelection: VehicleSelection;
  /** Called when vehicle selection changes */
  onVehicleSelectionChange: (selection: VehicleSelection) => void;
  /** Generic vehicle type options (from theme config) */
  vehicleTypeOptions: VehicleTypeOption[];
  /** User's saved vehicles (empty if not logged in) */
  savedVehicles: SavedVehicleOption[];
  /** Whether to show the saved vehicles section */
  showSavedVehicles: boolean;
  /** Called when "+ Add new vehicle" is clicked */
  onAddVehicle: () => void;
  /** Minimum date for date pickers */
  minDate?: Date;
  /**
   * Which date control the bar asks for, INDEPENDENTLY of `mode` (PB-5379).
   *
   * `'window'` is the enter/exit pair plus the duration select; `'permitStart'` is the single
   * start-date picker. Omitted derives it from `mode` exactly as every pre-PB-5379 caller does,
   * so the location layout is untouched.
   *
   * It exists as its own prop because `mode` stopped being able to answer the question: the
   * product layouts clamp `mode` to hourly (monthly mode drops monthly-disabled garages from the
   * sorted list while the product rows keep rendering, stranding them), so a permit-only product
   * search is in hourly mode while selling nothing that has an exit time. Un-clamping the mode to
   * reach the permit control would re-open that defect; this prop moves only the presentation.
   */
  datesMode?: 'window' | 'permitStart';
  /**
   * Earliest selectable PERMIT start. Falls back to `minDate` when omitted, which is what the
   * legacy monthly-mode path passes and where the two values are equal anyway.
   */
  permitMinDate?: Date;
  className?: string;
  /** Ref for the destination segment, used to align the location search panel */
  destinationRef?: React.RefObject<HTMLDivElement | null>;
  /**
   * Fires `true` when any of the search-bar date/time pickers opens and
   * `false` once all of them are closed. Used by the parent to suppress
   * side effects (e.g., a rate-refetch) until the user commits their time
   * selection by closing the picker.
   */
  onPickerOpenChange?: (open: boolean) => void;
  /** Drives picker gating: DATE/DATE_RANGE locks time, MONTH/MONTH_RANGE forces month-only. */
  eventDateRangeType?: EventDateRangeType;
  /**
   * The company's reservation search layout (PB-5345).
   *
   * ## Invariants:
   * - OPTIONAL, and omitting it must reproduce the pre-PB-5345 behaviour exactly — the
   *   Hourly/Monthly tabs render under their existing double gate. Every call site that predates
   *   this prop therefore compiles and behaves unchanged, which is what the legacy-lock tests
   *   assert. Do not make it required.
   */
  searchLayout?: ReservationSearchLayout;
}
